"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronRight, ArrowLeft, BookOpen, Clock, Lightbulb } from "lucide-react";
import { getArticle, categoryById, relatedArticles, readingMinutes } from "@/lib/help/articles";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { localizeArticle, localizeArticleOpt, localizeCategoryOpt, ui } from "@/lib/help/localized";
import { HelpArticleImages } from "@/components/help/HelpArticleImages";

/** Страница статьи базы знаний: хлебные крошки, разделы, оценка пользы, связанные. */
export function KbArticleClient({ slug, basePath = "/help" }: { slug: string; basePath?: string }) {
  const { locale } = useI18n();
  const article = localizeArticleOpt(getArticle(slug), locale);
  const [vote, setVote] = useState<"up" | "down" | null>(null);

  if (!article) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <p className="text-sm text-keiro-gray-600">{ui("notFound", locale)}</p>
        <Link href={basePath} className="inline-flex items-center gap-1.5 text-sm font-medium text-keiro-green-dark hover:underline">
          <ArrowLeft className="h-4 w-4" aria-hidden /> {ui("backToKb", locale)}
        </Link>
      </div>
    );
  }

  const category = localizeCategoryOpt(categoryById(article.category), locale);
  const related = relatedArticles(slug).map((a) => localizeArticle(a, locale));

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <nav className="flex flex-wrap items-center gap-1 text-xs text-keiro-gray-500" aria-label="Хлебные крошки">
        <Link href={basePath} className="inline-flex items-center gap-1 hover:text-keiro-green-dark">
          <BookOpen className="h-3.5 w-3.5" aria-hidden /> {ui("breadcrumb", locale)}
        </Link>
        {category ? (
          <>
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            <Link href={`${basePath}#cat-${category.id}`} className="hover:text-keiro-green-dark">
              {category.title}
            </Link>
          </>
        ) : null}
        <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        <span className="text-keiro-gray-700">{article.title}</span>
      </nav>

      <header>
        <h1 className="text-2xl font-semibold text-keiro-obsidian">{article.title}</h1>
        {article.intro ? <p className="mt-2 text-sm leading-relaxed text-keiro-gray-700">{article.intro}</p> : null}
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-keiro-gray-500">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" aria-hidden /> {readingMinutes(article)} {ui("readingMin", locale)}
          </span>
          {article.updated ? <span>{ui("updated", locale)} {article.updated}</span> : null}
        </div>
      </header>

      <HelpArticleImages images={article.images} />

      {article.sections.length > 1 ? (
        <div className="rounded-xl bg-keiro-mist/40 p-4">
          <p className="mb-1.5 text-caption font-semibold uppercase tracking-wide text-keiro-gray-600">{ui("inThisArticle", locale)}</p>
          <ul className="space-y-0.5 text-sm text-keiro-green-dark">
            {article.sections.map((s, i) => (
              <li key={i}>· {s.heading}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <article className="space-y-5">
        {article.sections.map((s, i) => (
          <section key={i}>
            <h2 className="mb-1.5 text-base font-semibold text-keiro-obsidian">{s.heading}</h2>
            {s.body.map((p, j) => (
              <p key={j} className="mb-2 text-sm leading-relaxed text-keiro-gray-700">
                {p}
              </p>
            ))}
            {s.steps && s.steps.length > 0 ? (
              <ol className="mb-2 mt-1 list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-keiro-gray-700 marker:font-semibold marker:text-keiro-green">
                {s.steps.map((st, k) => (
                  <li key={k} className="pl-1">{st}</li>
                ))}
              </ol>
            ) : null}
            {s.tip ? (
              <div className="mt-2 flex gap-2 rounded-lg border border-keiro-amber/30 bg-keiro-amber/10 p-3 text-sm text-keiro-obsidian">
                <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-keiro-amber" aria-hidden />
                <span>{s.tip}</span>
              </div>
            ) : null}
          </section>
        ))}
      </article>

      {article.faq && article.faq.length > 0 ? (
        <section aria-label="Частые вопросы" className="rounded-xl border border-keiro-green/10 bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-base font-semibold text-keiro-obsidian">{ui("faq", locale)}</h2>
          <dl className="space-y-3">
            {article.faq.map((f, i) => (
              <div key={i} className="border-b border-keiro-green/5 pb-3 last:border-0 last:pb-0">
                <dt className="text-sm font-medium text-keiro-obsidian">{f.q}</dt>
                <dd className="mt-1 text-sm leading-relaxed text-keiro-gray-700">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}

      <div className="rounded-xl border border-keiro-green/10 bg-white p-4 shadow-sm">
        {vote ? (
          <p className="text-sm text-keiro-green-dark">{ui("thanks", locale)}</p>
        ) : (
          <div className="flex items-center gap-3 text-sm">
            <span className="text-keiro-gray-600">{ui("helpful", locale)}</span>
            <button
              type="button"
              onClick={() => setVote("up")}
              className="rounded-lg border border-keiro-green/20 px-3 py-1 text-keiro-green-dark hover:bg-keiro-mist/50"
            >
              {ui("yes", locale)}
            </button>
            <button
              type="button"
              onClick={() => setVote("down")}
              className="rounded-lg border border-keiro-gray-200 px-3 py-1 text-keiro-gray-600 hover:bg-keiro-gray-100"
            >
              {ui("no", locale)}
            </button>
          </div>
        )}
      </div>

      {related.length > 0 ? (
        <section aria-label="Связанные статьи">
          <h2 className="mb-3 text-sm font-semibold text-keiro-obsidian">{ui("moreInCategory", locale)} «{category?.title}»</h2>
          <ul className="space-y-2">
            {related.map((a) => (
              <li key={a.slug}>
                <Link
                  href={`${basePath}/${a.slug}`}
                  className="group flex items-center justify-between gap-3 rounded-lg border border-keiro-green/10 bg-white px-4 py-3 shadow-sm transition-colors hover:border-keiro-green/30 hover:bg-keiro-mist/20"
                >
                  <span className="truncate text-sm font-medium text-keiro-obsidian group-hover:text-keiro-green-dark">
                    {a.title}
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-keiro-gray-400 group-hover:text-keiro-green" aria-hidden />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <Link href={basePath} className="inline-flex items-center gap-1.5 text-sm font-medium text-keiro-green-dark hover:underline">
        <ArrowLeft className="h-4 w-4" aria-hidden /> {ui("allKb", locale)}
      </Link>
    </div>
  );
}
